import { Check, CheckCircle, X } from "@phosphor-icons/react";
import { forwardRef, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import { IconButton } from "@/components/primitives/Button";
import { WarningAlertIcon, warningAccentBackgroundStyle, warningSoftBorderStyle, warningSurfaceTextStyle, warningTextStyle } from "@/components/ui/feedback/warningVisual";
import { cn } from "@/lib/cn";

export type HorizontalStepperState = "done" | "active" | "pending" | "disabled";
export type HorizontalStepperVariant = "primary" | "success" | "info" | "warning" | "neutral";
export type HorizontalStepperSize = "sm" | "md";

export type HorizontalStepperStep = {
  id?: string;
  label: ReactNode;
  secondaryText?: ReactNode;
  /** Backwards-compatible alias for secondaryText. Prefer secondaryText for new code. */
  description?: ReactNode;
  status?: HorizontalStepperState;
  disabled?: boolean;
};

export type HorizontalStepperProps = HTMLAttributes<HTMLElement> & {
  steps: HorizontalStepperStep[];
  currentStep?: number;
  title?: ReactNode;
  subheading?: ReactNode;
  /** Backwards-compatible alias for subheading. Prefer subheading for new code. */
  description?: ReactNode;
  announcement?: ReactNode;
  closeButton?: boolean;
  closeLabel?: string;
  onClose?: () => void;
  onStepSelect?: (step: HorizontalStepperStep, index: number) => void;
  size?: HorizontalStepperSize;
  surface?: "card" | "plain";
  variant?: HorizontalStepperVariant;
};

const sizeClassName = {
  sm: {
    section: "bfrs-px-5 bfrs-py-5 sm:bfrs-px-6",
    announcement: "bfrs-min-h-10 bfrs-rounded-md bfrs-py-2 bfrs-pl-3.5 bfrs-pr-4 bfrs-text-xs bfrs-leading-4",
    announcementWithClose: "bfrs-pr-12",
    announcementIcon: 18,
    closePosition: "bfrs-right-5 bfrs-top-5",
    closeIcon: 18,
    closeSize: "md" as const,
    steps: "bfrs-mt-7 bfrs-max-w-[420px]",
    stepsWithoutAnnouncement: "bfrs-mt-1",
    marker: "bfrs-size-7",
    checkIcon: 13,
    dot: "bfrs-size-2",
    connector: "bfrs-left-[calc(50%+28px)] bfrs-top-3.5 bfrs-w-[calc(100%-56px)]",
    stepGap: "bfrs-gap-2",
    label: "bfrs-text-[13px] bfrs-leading-5",
    stepDescription: "bfrs-text-[11px] bfrs-leading-4",
    textBlock: "bfrs-max-w-[124px]",
    content: "bfrs-mt-5 bfrs-gap-1",
    contentWithoutSteps: "bfrs-mt-4",
    title: "bfrs-text-[24px] bfrs-leading-tight sm:bfrs-text-[26px]",
    subheading: "bfrs-text-[15px] bfrs-leading-6"
  },
  md: {
    section: "bfrs-px-7 bfrs-py-7 sm:bfrs-px-9",
    announcement: "bfrs-min-h-12 bfrs-rounded-lg bfrs-py-2.5 bfrs-pl-5 bfrs-pr-5 bfrs-text-sm bfrs-leading-5",
    announcementWithClose: "bfrs-pr-14",
    announcementIcon: 22,
    closePosition: "bfrs-right-8 bfrs-top-8",
    closeIcon: 22,
    closeSize: "lg" as const,
    steps: "bfrs-mt-9 bfrs-max-w-[520px]",
    stepsWithoutAnnouncement: "bfrs-mt-2",
    marker: "bfrs-size-9",
    checkIcon: 16,
    dot: "bfrs-size-2.5",
    connector: "bfrs-left-[calc(50%+34px)] bfrs-top-[18px] bfrs-w-[calc(100%-68px)]",
    stepGap: "bfrs-gap-2.5",
    label: "bfrs-text-[15px] bfrs-leading-6",
    stepDescription: "bfrs-text-xs bfrs-leading-5",
    textBlock: "bfrs-max-w-[145px]",
    content: "bfrs-mt-6 bfrs-gap-1.5",
    contentWithoutSteps: "bfrs-mt-5",
    title: "bfrs-text-[28px] bfrs-leading-tight sm:bfrs-text-[32px]",
    subheading: "bfrs-text-[17px] bfrs-leading-7"
  }
} as const;

const variantClassName = {
  primary: {
    announcement: "bfrs-border-primary/40 bfrs-bg-primary-soft bfrs-text-primary",
    activeMarker: "bfrs-border-primary bfrs-text-primary",
    activeDot: "bfrs-bg-primary",
    doneMarker: "bfrs-border-primary bfrs-bg-primary bfrs-text-primary-contrast",
    activeText: "bfrs-text-primary"
  },
  success: {
    announcement: "bfrs-border-success/25 bfrs-bg-success-soft bfrs-text-success-contrast",
    activeMarker: "bfrs-border-success bfrs-text-success",
    activeDot: "bfrs-bg-success",
    doneMarker: "bfrs-border-success bfrs-bg-success bfrs-text-primary-contrast",
    activeText: "bfrs-text-success-contrast"
  },
  info: {
    announcement: "bfrs-border-info/25 bfrs-bg-info-soft bfrs-text-info-contrast",
    activeMarker: "bfrs-border-info bfrs-text-info",
    activeDot: "bfrs-bg-info",
    doneMarker: "bfrs-border-info bfrs-bg-info bfrs-text-primary-contrast",
    activeText: "bfrs-text-info-contrast"
  },
  warning: {
    announcement: "",
    activeMarker: "",
    activeDot: "",
    doneMarker: "",
    activeText: ""
  },
  neutral: {
    announcement: "bfrs-border-border-strong bfrs-bg-surface-muted bfrs-text-text-primary",
    activeMarker: "bfrs-border-text-secondary bfrs-text-text-secondary",
    activeDot: "bfrs-bg-text-secondary",
    doneMarker: "bfrs-border-text-secondary bfrs-bg-text-secondary bfrs-text-primary-contrast",
    activeText: "bfrs-text-text-primary"
  }
} as const;

function getStepState(step: HorizontalStepperStep, index: number, currentStep: number): HorizontalStepperState {
  if (step.disabled) return "disabled";
  if (step.status) return step.status;
  if (index < currentStep) return "done";
  if (index === currentStep) return "active";
  return "pending";
}

function StepMarker({
  state,
  size,
  variant
}: {
  state: HorizontalStepperState;
  size: HorizontalStepperSize;
  variant: HorizontalStepperVariant;
}) {
  const sizes = sizeClassName[size];
  const variants = variantClassName[variant];
  const isWarningVariant = variant === "warning";

  if (state === "done") {
    return (
      <span className={cn("bfrs-flex bfrs-items-center bfrs-justify-center bfrs-rounded-pill bfrs-border-2 bfrs-shadow-low", sizes.marker, variants.doneMarker)} style={isWarningVariant ? warningSurfaceTextStyle : undefined}>
        <Check size={sizes.checkIcon} weight="bold" />
      </span>
    );
  }

  const isActive = state === "active";

  return (
    <span
      className={cn(
        "bfrs-flex bfrs-items-center bfrs-justify-center bfrs-rounded-pill bfrs-border-2 bfrs-bg-surface-card bfrs-transition-colors bfrs-duration-200",
        sizes.marker,
        isActive ? variants.activeMarker : "bfrs-border-border-strong bfrs-text-text-secondary",
        state === "disabled" && "bfrs-opacity-55"
      )}
      style={isActive && isWarningVariant ? { ...warningSoftBorderStyle, ...warningTextStyle } : undefined}
    >
      <span className={cn("bfrs-rounded-pill", sizes.dot, isActive ? variants.activeDot : "bfrs-bg-text-secondary")} style={isActive && isWarningVariant ? warningAccentBackgroundStyle : undefined} />
    </span>
  );
}

export const HorizontalStepper = forwardRef<HTMLElement, HorizontalStepperProps>(
  (
    {
      className,
      steps,
      currentStep = 0,
      title,
      subheading,
      description,
      announcement,
      closeButton = false,
      closeLabel = "Close",
      onClose,
      onStepSelect,
      size = "sm",
      surface = "card",
      variant = "primary",
      style,
      ...props
    },
    ref
  ) => {
    const resolvedSteps = steps.map((step, index) => ({
      ...step,
      state: getStepState(step, index, currentStep)
    }));
    const stepGridStyle = {
      gridTemplateColumns: `repeat(${Math.max(resolvedSteps.length, 1)}, minmax(0, 1fr))`
    } as CSSProperties;
    const canClose = closeButton && Boolean(onClose);
    const resolvedSize = sizeClassName[size] ? size : "sm";
    const resolvedVariant = variantClassName[variant] ? variant : "primary";
    const sizes = sizeClassName[resolvedSize];
    const variants = variantClassName[resolvedVariant];
    const resolvedSubheading = subheading ?? description;

    return (
      <section
        ref={ref}
        className={cn(
          "bfrs-relative bfrs-w-full bfrs-bg-surface-card bfrs-text-text-primary",
          sizes.section,
          surface === "card" && "bfrs-rounded-xl bfrs-border bfrs-border-border bfrs-shadow-card",
          className
        )}
        style={style}
        aria-label={typeof title === "string" ? title : "Step progress"}
        {...props}
      >
        {announcement && (
          <div className={cn("bfrs-flex bfrs-items-center bfrs-border bfrs-font-bold", sizes.announcement, variants.announcement, canClose && sizes.announcementWithClose)} style={resolvedVariant === "warning" ? warningSurfaceTextStyle : undefined}>
            {resolvedVariant === "warning" ? (
              <span className="bfrs-mr-2 bfrs-flex bfrs-shrink-0">
                <WarningAlertIcon />
              </span>
            ) : (
              <CheckCircle size={sizes.announcementIcon} weight="regular" className="bfrs-mr-2 bfrs-shrink-0" />
            )}
            <span className="bfrs-min-w-0">{announcement}</span>
          </div>
        )}

        {canClose && (
          <IconButton
            aria-label={closeLabel}
            icon={<X size={sizes.closeIcon} />}
            size={sizes.closeSize}
            variant="ghost"
            className={cn("bfrs-absolute bfrs-text-text-primary hover:bfrs-bg-surface-muted", sizes.closePosition)}
            onClick={onClose}
          />
        )}

        {resolvedSteps.length > 0 && (
          <ol
            className={cn(
              "bfrs-mx-auto bfrs-grid bfrs-w-full bfrs-items-start",
              sizes.steps,
              !announcement && sizes.stepsWithoutAnnouncement
            )}
            style={stepGridStyle}
            aria-live="polite"
          >
            {resolvedSteps.map((step, index) => {
              const isInteractive = Boolean(onStepSelect) && !step.disabled;
              const isMuted = step.state === "pending" || step.state === "disabled";
              const stepKey = step.id ?? index;
              const marker = <StepMarker state={step.state} size={resolvedSize} variant={resolvedVariant} />;
              const stepSecondaryText = step.secondaryText ?? step.description;

              return (
                <li
                  key={stepKey}
                  className="bfrs-relative bfrs-flex bfrs-min-w-0 bfrs-flex-col bfrs-items-center bfrs-text-center"
                  aria-current={step.state === "active" ? "step" : undefined}
                >
                  {index < resolvedSteps.length - 1 && (
                    <span className={cn("bfrs-absolute bfrs-h-0.5 bfrs-bg-border-strong", sizes.connector)} aria-hidden="true" />
                  )}
                  {isInteractive ? (
                    <button
                      type="button"
                      className={cn(
                        "bfrs-relative bfrs-z-10 bfrs-grid bfrs-justify-items-center bfrs-rounded-lg bfrs-outline-none bfrs-transition bfrs-duration-150 hover:bfrs-text-primary focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus focus-visible:bfrs-ring-offset-2 focus-visible:bfrs-ring-offset-surface-card",
                        sizes.stepGap
                      )}
                      onClick={() => onStepSelect?.(step, index)}
                    >
                      {marker}
                      <span className={cn("bfrs-grid bfrs-gap-1", sizes.textBlock)}>
                        <span className={cn("bfrs-font-bold", sizes.label, isMuted ? "bfrs-text-text-secondary" : variants.activeText)} style={!isMuted && resolvedVariant === "warning" ? warningTextStyle : undefined}>{step.label}</span>
                        {stepSecondaryText && <span className={cn("bfrs-font-normal bfrs-text-text-muted", sizes.stepDescription)}>{stepSecondaryText}</span>}
                      </span>
                    </button>
                  ) : (
                    <div className={cn("bfrs-relative bfrs-z-10 bfrs-grid bfrs-justify-items-center", sizes.stepGap)}>
                      {marker}
                      <span className={cn("bfrs-grid bfrs-gap-1", sizes.textBlock)}>
                        <span className={cn("bfrs-font-bold", sizes.label, isMuted ? "bfrs-text-text-secondary" : variants.activeText)} style={!isMuted && resolvedVariant === "warning" ? warningTextStyle : undefined}>{step.label}</span>
                        {stepSecondaryText && <span className={cn("bfrs-font-normal bfrs-text-text-muted", sizes.stepDescription)}>{stepSecondaryText}</span>}
                      </span>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}

        {(title || resolvedSubheading) && (
          <div className={cn("bfrs-grid", sizes.content, resolvedSteps.length === 0 && sizes.contentWithoutSteps)}>
            {title && <h3 className={cn("bfrs-font-bold bfrs-text-text-primary", sizes.title)}>{title}</h3>}
            {resolvedSubheading && <p className={cn("bfrs-font-semibold bfrs-text-text-secondary", sizes.subheading)}>{resolvedSubheading}</p>}
          </div>
        )}
      </section>
    );
  }
);

HorizontalStepper.displayName = "HorizontalStepper";
